import { useEffect, useMemo, useState } from 'react';

const emptyArticle = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  status: 'draft',
  publishDate: '',
  author: '',
  featuredImage: '',
  ctaHeadline: '',
  ctaText: '',
  ctaButtonText: '',
  ctaButtonLink: '',
  seoTitle: '',
  seoDescription: ''
};

function slugify(value = '') {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export default function ArticleEditorForm({ initialArticle, onSubmit, busy, submitLabel = 'Save Article' }) {
  const [article, setArticle] = useState({ ...emptyArticle, ...(initialArticle || {}) });
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setArticle({ ...emptyArticle, ...(initialArticle || {}) });
  }, [initialArticle]);

  const canSubmit = useMemo(
    () => article.title?.trim() && article.slug?.trim() && article.excerpt?.trim() && article.body?.trim(),
    [article]
  );

  const setField = (field, value) => {
    setArticle((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={(event) => { event.preventDefault(); onSubmit(article); }} className="space-y-5 rounded-2xl bg-white p-6 ring-1 ring-slate-200">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700">Title *</label>
          <input
            value={article.title}
            onChange={(e) => {
              const nextTitle = e.target.value;
              setField('title', nextTitle);
              if (!initialArticle?.slug || !article.slug || article.slug === slugify(article.title)) {
                setField('slug', slugify(nextTitle));
              }
            }}
            required
            className="mt-1 w-full rounded-lg border-slate-300"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Slug *</label>
          <input
            value={article.slug}
            onChange={(e) => setField('slug', slugify(e.target.value))}
            required
            className="mt-1 w-full rounded-lg border-slate-300"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700">Publish Date *</label>
          <input type="date" value={article.publishDate || ''} onChange={(e) => setField('publishDate', e.target.value)} required className="mt-1 w-full rounded-lg border-slate-300" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Author (optional)</label>
          <input value={article.author || ''} onChange={(e) => setField('author', e.target.value)} className="mt-1 w-full rounded-lg border-slate-300" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">Featured Image URL (optional)</label>
        <input value={article.featuredImage || ''} onChange={(e) => setField('featuredImage', e.target.value)} placeholder="https://..." className="mt-1 w-full rounded-lg border-slate-300" />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">Excerpt *</label>
        <textarea value={article.excerpt} onChange={(e) => setField('excerpt', e.target.value)} rows={3} required className="mt-1 w-full rounded-lg border-slate-300" />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="block text-sm font-medium text-slate-700">Body (Markdown supported) *</label>
          <button type="button" className="text-sm text-cam-blue hover:underline" onClick={() => setPreviewOpen((prev) => !prev)}>
            {previewOpen ? 'Hide preview' : 'Show preview'}
          </button>
        </div>
        <textarea value={article.body} onChange={(e) => setField('body', e.target.value)} rows={14} required className="mt-1 w-full rounded-lg border-slate-300 font-mono text-sm" />
        {previewOpen ? (
          <div className="mt-4 rounded-xl bg-slate-50 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cam-blue/70">Preview</p>
            <div className="whitespace-pre-wrap text-slate-700">{article.body || 'Start writing to preview your content.'}</div>
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700">Status</label>
          <select value={article.status} onChange={(e) => setField('status', e.target.value)} className="mt-1 w-full rounded-lg border-slate-300">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cam-blue/70">Article CTA (optional)</h3>
        <div className="mt-3 space-y-3">
          <input value={article.ctaHeadline || ''} onChange={(e) => setField('ctaHeadline', e.target.value)} placeholder="CTA headline" className="w-full rounded-lg border-slate-300" />
          <textarea value={article.ctaText || ''} onChange={(e) => setField('ctaText', e.target.value)} placeholder="CTA supporting text" rows={2} className="w-full rounded-lg border-slate-300" />
          <div className="grid gap-3 md:grid-cols-2">
            <input value={article.ctaButtonText || ''} onChange={(e) => setField('ctaButtonText', e.target.value)} placeholder="CTA button text" className="w-full rounded-lg border-slate-300" />
            <input value={article.ctaButtonLink || ''} onChange={(e) => setField('ctaButtonLink', e.target.value)} placeholder="CTA button link" className="w-full rounded-lg border-slate-300" />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cam-blue/70">SEO (optional)</h3>
        <div className="mt-3 space-y-3">
          <input value={article.seoTitle || ''} onChange={(e) => setField('seoTitle', e.target.value)} placeholder="SEO title" className="w-full rounded-lg border-slate-300" />
          <textarea value={article.seoDescription || ''} onChange={(e) => setField('seoDescription', e.target.value)} rows={2} placeholder="SEO description" className="w-full rounded-lg border-slate-300" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button disabled={busy || !canSubmit} className="rounded-lg bg-cam-blue px-4 py-2 text-white disabled:opacity-70">{busy ? 'Saving...' : submitLabel}</button>
        {!canSubmit ? <span className="text-sm text-slate-500">Complete title, slug, excerpt, and body.</span> : null}
      </div>
    </form>
  );
}
